import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TopBarRight from './common/TopBarRight';
import '../styles/Navbar.css';

const Navbar = () => {
  const location = useLocation();
  const { isAuthenticated, user, isLoading } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  const isActiveRoute = (path) => {
    if (location.pathname === path) return true;
    if (path === '/geoguesser') {
      return location.pathname.startsWith(path + '/') && !location.pathname.startsWith('/geoguesser/leaderboards');
    }
    return location.pathname.startsWith(path + '/');
  };

  const isMoreSectionActive = () =>
    isActiveRoute('/teams') ||
    location.pathname === '/badges' ||
    isActiveRoute('/geoguesser/leaderboards') ||
    location.pathname === '/about' ||
    location.pathname === '/changelog' ||
    (!isLoading && isAuthenticated && (user?.is_staff || user?.is_superuser) && isActiveRoute('/admin'));

  const renderNavLinks = (onClick) => (
    <>
      <Link
        to="/"
        className={`navbar-link site-nav-link ${isActiveRoute('/') && location.pathname === '/' ? 'active' : ''}`}
        onClick={onClick}
      >
        Home
      </Link>
      <Link
        to="/maps"
        className={`navbar-link site-nav-link ${isActiveRoute('/maps') ? 'active' : ''}`}
        onClick={onClick}
      >
        Maps
      </Link>
      <Link
        to="/geoguesser"
        className={`navbar-link site-nav-link ${isActiveRoute('/geoguesser') ? 'active' : ''}`}
        onClick={onClick}
      >
        Geoguesser
      </Link>
      <Link
        to="/geoguesser/leaderboards"
        className={`navbar-link site-nav-link ${isActiveRoute('/geoguesser/leaderboards') ? 'active' : ''}`}
        onClick={onClick}
        title="Geoguesser leaderboards"
      >
        Ranks
      </Link>
      <Link
        to="/tournament-tools"
        className={`navbar-link site-nav-link ${isActiveRoute('/tournament-tools') ? 'active' : ''}`}
        onClick={onClick}
      >
        Tournaments
      </Link>
      <Link
        to="/teams"
        className={`navbar-link site-nav-link ${isActiveRoute('/teams') ? 'active' : ''}`}
        onClick={onClick}
      >
        Teams
      </Link>
      <Link
        to="/badges"
        className={`navbar-link site-nav-link ${location.pathname === '/badges' ? 'active' : ''}`}
        onClick={onClick}
      >
        Badges
      </Link>
      {!isLoading && isAuthenticated && (user?.is_staff || user?.is_superuser) && (
        <Link
          to="/admin"
          className={`navbar-link site-nav-link ${isActiveRoute('/admin') ? 'active' : ''}`}
          onClick={onClick}
        >
          Admin
        </Link>
      )}
    </>
  );

  return (
    <>
      <nav className="navbar" aria-label="Main">
        <div className="navbar-container">
          <Link to="/" className="navbar-brand" aria-label="Strat Guys home">
            <img
              className="brand-logo"
              src={`${process.env.PUBLIC_URL || ''}/stratguys-favicon.jpg`}
              alt=""
              width="32"
              height="32"
            />
            <span className="brand-text">Strat Guys</span>
          </Link>

          <div className="navbar-menu">
            {renderNavLinks()}
          </div>

          <TopBarRight />
        </div>
      </nav>

      {moreOpen && (
        <button
          type="button"
          className="navbar-more-backdrop"
          aria-label="Close menu"
          onClick={() => setMoreOpen(false)}
        />
      )}

      <div
        id="navbar-more-panel"
        className={`navbar-more-sheet ${moreOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-label="More navigation"
        aria-hidden={!moreOpen}
      >
        <div className="navbar-more-sheet-inner">
          <Link
            to="/teams"
            className={`navbar-more-link ${isActiveRoute('/teams') ? 'active' : ''}`}
            onClick={() => setMoreOpen(false)}
          >
            Teams
          </Link>
          <Link
            to="/badges"
            className={`navbar-more-link ${location.pathname === '/badges' ? 'active' : ''}`}
            onClick={() => setMoreOpen(false)}
          >
            Badges
          </Link>
          <Link
            to="/geoguesser/leaderboards"
            className={`navbar-more-link ${isActiveRoute('/geoguesser/leaderboards') ? 'active' : ''}`}
            onClick={() => setMoreOpen(false)}
          >
            Ranks
          </Link>
          <Link
            to="/about"
            className={`navbar-more-link ${location.pathname === '/about' ? 'active' : ''}`}
            onClick={() => setMoreOpen(false)}
          >
            About
          </Link>
          <Link
            to="/changelog"
            className={`navbar-more-link ${location.pathname === '/changelog' ? 'active' : ''}`}
            onClick={() => setMoreOpen(false)}
          >
            Changelog
          </Link>
          {!isLoading && isAuthenticated && (user?.is_staff || user?.is_superuser) && (
            <Link
              to="/admin"
              className={`navbar-more-link ${isActiveRoute('/admin') ? 'active' : ''}`}
              onClick={() => setMoreOpen(false)}
            >
              Admin
            </Link>
          )}
        </div>
      </div>

      <nav className="navbar-bottom" aria-label="Mobile primary">
        <Link
          to="/"
          className={`navbar-bottom-link ${location.pathname === '/' ? 'active' : ''}`}
          aria-current={location.pathname === '/' ? 'page' : undefined}
        >
          <span className="navbar-bottom-label">Home</span>
        </Link>
        <Link
          to="/maps"
          className={`navbar-bottom-link ${isActiveRoute('/maps') ? 'active' : ''}`}
          aria-current={isActiveRoute('/maps') ? 'page' : undefined}
        >
          <span className="navbar-bottom-label">Maps</span>
        </Link>
        <Link
          to="/geoguesser"
          className={`navbar-bottom-link ${isActiveRoute('/geoguesser') ? 'active' : ''}`}
          aria-current={isActiveRoute('/geoguesser') ? 'page' : undefined}
        >
          <span className="navbar-bottom-label">Geo</span>
        </Link>
        <Link
          to="/tournament-tools"
          className={`navbar-bottom-link ${isActiveRoute('/tournament-tools') ? 'active' : ''}`}
          aria-current={isActiveRoute('/tournament-tools') ? 'page' : undefined}
        >
          <span className="navbar-bottom-label">Tourneys</span>
        </Link>
        <button
          type="button"
          className={`navbar-bottom-link navbar-bottom-more-btn ${moreOpen || isMoreSectionActive() ? 'active' : ''}`}
          aria-label="More navigation"
          aria-expanded={moreOpen}
          aria-controls="navbar-more-panel"
          onClick={() => setMoreOpen((o) => !o)}
        >
          <span className="navbar-bottom-label">More</span>
        </button>
      </nav>
    </>
  );
};

export default Navbar;
